import { EditOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Checkbox, DatePicker, message, Popconfirm, Space, Spin, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fmtDate } from "@extensions/convert";
import { progKeysStatusRender } from "@extensions/enums";
import { renderCheckbox, renderDate } from "../../extensions/helper";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import { serverFetch } from "../../server";
import "./license-order-keys.css";
import LicenseOrderKey from "./license-order-key";

const statusTagColor = s => {
  const map = { 0: "default", 1: "blue", 2: "green", 3: "cyan", 4: "orange", 5: "red" };
  return map[s] ?? "default";
};

const LicenseOrderKeys = ({ companyId, accId, onClose }) => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [finDate, setFinDate] = useState(dayjs());
  const user = useSelector(s => s.user);

  const getData = () => {
    if (!companyId || !accId) return;

    setLoading(true);
    serverFetch(`license/orderkeys?companyId=${companyId}&accId=${accId}`)
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    if (companyId && accId) getData();
  }, [companyId, accId]);

  const onRowClick = record => {
    setSelectedKey({
      keyId: record.id,
    });
  };

  const handleKeyModalClose = refresh => {
    setSelectedKey(null);
    if (refresh) getData();
  };

  const toggleRowKey = id => {
    setSelectedRowKeys(prev =>
      prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]
    );
  };

  const handleSetFinDate = () => {
    setLoading(true);

    const requestBody = {
      companyId,
      accId,
      finDate: finDate.format("YYYY-MM-DD"),
    };

    if (selectedRowKeys.length > 0) {
      requestBody.keyIds = selectedRowKeys;
    }

    serverFetch(`license/setkeyfindate`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then(() => {
        setLoading(false);
        message.success("Дата окончания установлена");
        setSelectedRowKeys([]);
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const handleSetFrozen = () => {
    setLoading(true);

    const requestBody = {
      companyId,
      accId,
      frozen: true,
    };

    if (selectedRowKeys.length > 0) {
      requestBody.keyIds = selectedRowKeys;
    }

    serverFetch(`license/setfrozen`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then(() => {
        setLoading(false);
        message.success("Ключи заморожены");
        setSelectedRowKeys([]);
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const handleSetUnfrozen = () => {
    setLoading(true);

    const requestBody = {
      companyId,
      accId,
      frozen: false,
    };

    if (selectedRowKeys.length > 0) {
      requestBody.keyIds = selectedRowKeys;
    }

    serverFetch(`license/setfrozen`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then(() => {
        setLoading(false);
        message.success("Ключи разморожены");
        setSelectedRowKeys([]);
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 60 },
    { dataIndex: "status", title: "Статус", render: progKeysStatusRender },
    { dataIndex: "startDate", title: "Активирован", render: renderDate },
    { dataIndex: "finDate", title: "Истекает", render: renderDate },
    { dataIndex: "comment", title: "Комментарий" },
    { dataIndex: "frozen", title: "Заморожен", render: renderCheckbox },
    { dataIndex: "sent", title: "Отправлен", render: renderCheckbox },
  ];

  const adminSetFinDateBtn = (
    <Popconfirm
      title="Установить дату окончания"
      description={
        selectedRowKeys.length === 0
          ? `Установить дату окончания ${finDate.format("DD.MM.YYYY")} для всех ключей заказа?`
          : `Установить дату окончания ${finDate.format("DD.MM.YYYY")} для ${selectedRowKeys.length} выбранных ключей?`
      }
      onConfirm={handleSetFinDate}
      okText="Да"
      cancelText="Отмена"
    >
      <Button type="primary" loading={loading}>
        Установить дату окончания
      </Button>
    </Popconfirm>
  );

  const adminFreezeBtn = (
    <Popconfirm
      title="Заморозить ключи"
      description={
        selectedRowKeys.length === 0
          ? "Заморозить все ключи заказа?"
          : `Заморозить ${selectedRowKeys.length} выбранных ключей?`
      }
      onConfirm={handleSetFrozen}
      okText="Да"
      cancelText="Отмена"
    >
      <Button type="default" loading={loading}>
        Заморозить
      </Button>
    </Popconfirm>
  );

  const adminUnfreezeBtn = (
    <Popconfirm
      title="Разморозить ключи"
      description={
        selectedRowKeys.length === 0
          ? "Разморозить все ключи заказа?"
          : `Разморозить ${selectedRowKeys.length} выбранных ключей?`
      }
      onConfirm={handleSetUnfrozen}
      okText="Да"
      cancelText="Отмена"
    >
      <Button type="default" loading={loading}>
        Разморозить
      </Button>
    </Popconfirm>
  );

  const adminToolbar = user?.admin ? (
    isMobile ? (
      <div className="license-order-keys-admin-toolbar">
        <div className="license-order-keys-admin-toolbar__row1">
          <DatePicker
            className="license-order-keys-admin-toolbar__date"
            format="DD.MM.YYYY"
            value={finDate}
            onChange={setFinDate}
            allowClear={false}
          />
          <div className="license-order-keys-admin-toolbar__setdate-wrap">{adminSetFinDateBtn}</div>
        </div>
        <div className="license-order-keys-admin-toolbar__row2">
          {adminFreezeBtn}
          {adminUnfreezeBtn}
        </div>
      </div>
    ) : (
      <Space style={{ marginBottom: 16 }} wrap>
        <DatePicker format="DD.MM.YYYY" value={finDate} onChange={setFinDate} allowClear={false} />
        {adminSetFinDateBtn}
        {adminFreezeBtn}
        {adminUnfreezeBtn}
      </Space>
    )
  ) : null;

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="license-order-keys-mobile-list">
        {items.map(record => {
          const statusLabel = progKeysStatusRender(record.status) ?? "—";
          const comment = record.comment != null ? String(record.comment).trim() : "";
          const showFlags = record.frozen || record.sent;
          const checked = selectedRowKeys.includes(record.id);

          return (
            <div
              key={record.id}
              className={`license-order-keys-mobile-card${showFlags ? " license-order-keys-mobile-card--flags" : ""}`}
              role="button"
              tabIndex={0}
              onClick={e => {
                if (e.target.closest(".license-order-keys-mobile-card-edit")) return;
                if (e.target.closest(".ant-checkbox-wrapper")) return;
                toggleRowKey(record.id);
              }}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!e.target.closest(".license-order-keys-mobile-card-edit")) toggleRowKey(record.id);
                }
              }}
            >
              <div className="license-order-keys-mobile-corner-wrap">
                {showFlags ? (
                  <div className="license-order-keys-mobile-flag-icons" aria-hidden>
                    {record.frozen ? (
                      <span className="license-order-keys-mobile-frozen" title="Заморожен">
                        &#10052;
                      </span>
                    ) : null}
                    {record.sent ? (
                      <MailOutlined className="license-order-keys-mobile-mail" title="Отправлен" aria-label="Отправлен" />
                    ) : null}
                  </div>
                ) : null}
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  className="license-order-keys-mobile-card-edit"
                  aria-label="Редактировать"
                  onClick={e => {
                    e.stopPropagation();
                    onRowClick(record);
                  }}
                />
              </div>
              <div className="license-order-keys-mobile-row1">
                <Checkbox
                  checked={checked}
                  onChange={e => {
                    e.stopPropagation();
                    setSelectedRowKeys(prev =>
                      e.target.checked
                        ? [...new Set([...prev, record.id])]
                        : prev.filter(k => k !== record.id)
                    );
                  }}
                  onClick={e => e.stopPropagation()}
                />
                <div className="license-order-keys-mobile-row1-main">
                  <Tag color={statusTagColor(record.status)}>{statusLabel}</Tag>
                </div>
              </div>
              <div className="license-order-keys-mobile-row2">
                {fmtDate(record.startDate) || "—"}{" "}
                <span style={{ color: "var(--border-theme-light)" }} aria-hidden>
                  →
                </span>{" "}
                {fmtDate(record.finDate) || "—"}
              </div>
              <div className="license-order-keys-mobile-row3">{comment || "—"}</div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  const tableEl = (
    <Table
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey="id"
      pagination={false}
      size="small"
      className="cloud-modal-table"
      scroll={{ y: "calc(100vh - 200px)" }}
      rowSelection={rowSelection}
      onRow={record => ({
        onClick: () => onRowClick(record),
        style: { cursor: "pointer" },
      })}
    />
  );

  return (
    <>
      {selectedKey && (
        <LicenseOrderKey
          companyId={companyId}
          accId={accId}
          keyId={selectedKey.keyId}
          onClose={handleKeyModalClose}
        />
      )}

      {adminToolbar}

      {isMobile ? mobileListEl : tableEl}
    </>
  );
};

export default LicenseOrderKeys;
